import type { ProtectionProbe, ViewOnlyFileProtection } from "../../store/state/user/types";
import { decryptProtectedUrl, extractEncryptedParameter, hasNonSasParameter, isWebCryptoAvailable } from "./decryptUrl";

export type ResolveFailureReason = "unverified" | "no-crypto" | "undecryptable";

export type ResolvedFileUrl = { status: "resolved"; url: string } | { status: "error"; reason: ResolveFailureReason };

export interface ResolveProtectedFileUrlDeps {
  protection: ViewOnlyFileProtection | undefined;
  refreshProtection: () => Promise<ProtectionProbe>;
}

// Decided from the URL alone — a plain URL must never cost a /User/permissions probe.
export const mustResolveUrl = (url: string): boolean => hasNonSasParameter(url);

// `isResolving` matters because useFetch holds the previous value across a refetch.
export const selectEffectiveUrl = (
  url: string,
  state: { mustResolve: boolean; isResolving: boolean; resolved: string | undefined }
): string | undefined => (state.mustResolve ? (state.isResolving ? undefined : state.resolved) : url);

// Never returns the visible URL on failure — under enforcement that URL is a blank decoy.
export const resolveProtectedFileUrl = async (
  url: string,
  deps: ResolveProtectedFileUrlDeps
): Promise<ResolvedFileUrl> => {
  if (!mustResolveUrl(url)) {
    return { status: "resolved", url };
  }

  const held = deps.protection;
  const probe: ProtectionProbe = held ? { answered: true, protection: held } : await deps.refreshProtection();
  if (!probe.answered) {
    return { status: "error", reason: "unverified" };
  }

  const protection = probe.protection;
  if (!protection) {
    return { status: "resolved", url };
  }

  const encryptedParameter = extractEncryptedParameter(url, protection.parameterName);
  if (!encryptedParameter) {
    return { status: "resolved", url };
  }

  if (!isWebCryptoAvailable()) {
    return { status: "error", reason: "no-crypto" };
  }

  const decrypted = await decryptProtectedUrl(encryptedParameter, protection.keys);
  if (decrypted) {
    return { status: "resolved", url: decrypted };
  }

  if (held) {
    // Held keys can be a rotation behind — refetch once; keys just fetched are not refetched.
    const refreshed = await deps.refreshProtection();
    const retried =
      refreshed.answered && refreshed.protection
        ? await decryptProtectedUrl(encryptedParameter, refreshed.protection.keys)
        : undefined;
    if (retried) {
      return { status: "resolved", url: retried };
    }
  }

  return { status: "error", reason: "undecryptable" };
};
